
import {
    SeverityLevel,
    Scope,
    ErrorEvent,
    init,
    captureMessage,
    captureException,
    withScope,
    eventFiltersIntegration,
    httpContextIntegration,
} from '@sentry/browser'
import { UrlParams } from './urlparams';
import { VERSION } from './version';
import { ENVIRONMENT } from './environment';
import { safeLocationHostname } from './safeglobals';

export const SENTRY_DSN = 'https://da8e408f13b70b78e09419156fab9ab7@o4507056858988544.ingest.us.sentry.io/4507057035149312';

/**
 * Optional searchable/structured data for a single report.
 *
 * The split is deliberate and load-bearing: Sentry indexes TAGS, so only tags can
 * be searched, grouped and charted, while CONTEXTS are viewable detail. Anything
 * you need to answer "is this one client or all of them?" has to be a tag, and
 * anything high-cardinality (a full URL, an attempt log) must not be.
 */
export interface TelemetryScopeData {
    tags?: Record<string, string>;
    contexts?: Record<string, Record<string, any>>;
    fingerprint?: string[];
}

/**
 * Truncate a URL at its query string or fragment, whichever comes first.
 *
 * Avoids the URL constructor: it is absent on IE11, and a throw inside beforeSend
 * would drop the event entirely.
 */
function cutQueryAndFragment(value: string) : string {
    const queryStart = value.indexOf('?');
    const fragmentStart = value.indexOf('#');
    let cut = queryStart;
    if (cut === -1 || (fragmentStart !== -1 && fragmentStart < cut)) {
        cut = fragmentStart;
    }
    return cut === -1 ? value : value.substring(0, cut);
}

/**
 * Strip personal data out of the request context before transmission.
 *
 * `httpContextIntegration` is enabled so Sentry can attribute browser and OS, which
 * needs the request block. But it reports the full page URL and a Referer header,
 * and a client's form thank-you page routinely carries personal data in the query
 * string of both. This removes the query string and truncates each URL.
 *
 * Mandatory, not optional: the integration must never ship without it.
 */
export function scrubRequestUrl(event: ErrorEvent) : ErrorEvent {
    try {
        const request = event.request;
        if (!request) {
            return event;
        }
        delete request.query_string;
        // httpContextIntegration also sets a Referer header from document.referrer,
        // which on a client's thank-you page can carry personal data in its query
        // string just as the page URL can. Scrub it the same way.
        const headers = request.headers as Record<string, string> | undefined;
        if (headers && typeof headers.Referer === 'string') {
            headers.Referer = cutQueryAndFragment(headers.Referer);
        }
        if (typeof request.url === 'string') {
            request.url = cutQueryAndFragment(request.url);
        }
    } catch (ignored) {
        // If scrubbing fails partway the request block cannot be trusted, so drop
        // it wholesale rather than risk transmitting a query string. Losing the
        // browser attribution is strictly better than leaking personal data.
        try {
            delete event.request;
        } catch (alsoIgnored) {
            // nothing further we can safely do
        }
    }
    return event;
}

export enum LogLevelEnum {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    CRITICAL = 4,
}

export enum TelemetryExitCode {
    SUCCESS = 0,
    FATAL = 1,
    REJECTED = 2,
}

export class Telemetry
{

    private logLevel: LogLevelEnum;
    private credentialString : string;

    constructor(logLevel: LogLevelEnum = LogLevelEnum.WARN, credentialString: string = '') {
        this.logLevel = logLevel;
        this.credentialString = credentialString;
        init(
            {
                dsn: credentialString,
                // Without this, prod and non-prod share one Sentry environment.
                environment: ENVIRONMENT,
                // Tag EVERY event from every path with the client site, so failures
                // can be segmented and alerted on per client. Set here rather than
                // per call site so nothing can forget it.
                initialScope: {
                    tags: { client_hostname: safeLocationHostname() },
                },
                defaultIntegrations: false,
                // eventFiltersIntegration is re-enabled so that allowUrls filtering
                // is enforced without auto-capturing unhandled errors from
                // third-party scripts on the page (that is globalHandlers, which
                // stays off). httpContextIntegration is re-enabled so Sentry can
                // attribute browser/OS - it only adds the request block and does
                // NOT auto-capture anything.
                integrations: [eventFiltersIntegration(), httpContextIntegration()],
                // Mandatory companion to httpContextIntegration: removes the query
                // string, which can carry personal data on a client's page.
                beforeSend: scrubRequestUrl,
                // Without frames, eventFilters cannot evaluate allowUrls, so WARN
                // captureMessage events bypass the origin gate entirely - a localhost
                // or self-hosted bundle would pollute the recovery-rate denominator.
                attachStacktrace: true,
                // Only report errors originating from fountain-analytics.ts.
                //
                // TWO origins per environment, deliberately. The domain is the normal
                // deployment; the storage.googleapis.com bucket URL is break-glass,
                // used if Cloudflare is having an outage. Both must be listed because
                // this gate drops everything else, and since `attachStacktrace` is on
                // it applies to WARN captureMessage events too - so a missing origin
                // silently removes that environment's alerts AND its recovery-rate
                // denominator, during precisely the incident you switched origins for.
                //
                // Same reasoning is why the control probe derives its URL from the
                // running bundle's own directory instead of hardcoding a host: see
                // CONTROL_PROBE_URL in ./httpdiagnostics.
                //
                // Builds from this repository are served only from the D12 hosts
                // (BACKLOG.md §9.13); analytics.fonterra.io is frozen at 2.9.0, whose
                // own bundle keeps its own list. The trailing slash stops a prefix
                // match on a look-alike host such as web.fonterra.io.example.com.
                allowUrls: [
                    'https://web.fonterra.io/',
                    'https://nonprod-web.fonterra.io/',
                    'https://storage.googleapis.com/web.fonterra.io/',
                    'https://storage.googleapis.com/nonprod-web.fonterra.io/',
                ],
                release: VERSION,
                // Performance Monitoring
                // tracesSampleRate: 1.0,
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
                // Session Replay
                // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            }
        );
    }

    public checkForTest() : void 
    {
        const kswitch = UrlParams.getOneOf(['fntn-tel-test'], UrlParams.parse());
        if (kswitch.length) {
            this.log(LogLevelEnum.CRITICAL, 'Telemetry test', {});
        }
    }

    /**
     * Apply the optional searchable data for one report.
     *
     * Each group is guarded separately rather than wrapping the whole method in
     * one try/catch: a single malformed tag must not also cost us the contexts and
     * the fingerprint, which is where the diagnostic detail lives.
     */
    private applyScopeData(scope: Scope, level: SeverityLevel, data?: TelemetryScopeData) : void
    {
        try {
            // Without this the SDK falls back to its default and ERROR is
            // indistinguishable from CRITICAL - i.e. "degraded" reads the same as
            // "broken". See REFERENCE.md §11 invariant 7.
            scope.setLevel(level);
        } catch (ignored) {}
        if (!data) {
            return;
        }
        try {
            if (data.tags) {
                scope.setTags(data.tags);
            }
        } catch (ignored) {}
        try {
            if (data.contexts) {
                for (const name in data.contexts) {
                    if (Object.prototype.hasOwnProperty.call(data.contexts, name)) {
                        scope.setContext(name, data.contexts[name]);
                    }
                }
            }
        } catch (ignored) {}
        try {
            if (data.fingerprint) {
                scope.setFingerprint(data.fingerprint);
            }
        } catch (ignored) {}
    }

    public async log(
        level: LogLevelEnum,
        message: string,
        context: Record<string, string | number | null>,
        error?: Error,
        scopeData?: TelemetryScopeData
    ) : Promise<TelemetryExitCode>
    {
        if (!this.credentialString) {
            return TelemetryExitCode.REJECTED;
        }
        try {
            return await this.tryLog(level, message, context, error, scopeData);
        } catch (error) {
            console.error(error);
            return TelemetryExitCode.FATAL;
        }
    }

    public async tryLog(
        level: LogLevelEnum,
        message: string,
        context: Record<string, string | number | null>,
        error?: Error,
        scopeData?: TelemetryScopeData
    ) : Promise<TelemetryExitCode>
    {
        message = `Fountain::Telemetry: ${message}`;
        if (level >= this.logLevel) {
            const timestamp = Date.now();
            const body: Record<string, string | number | null | Record<string, string | number | null>> = {
                timestamp: timestamp,
                message: message,
                logtype: 'javascript',
                service: 'fountain-analytics',
                hostname: safeLocationHostname(),
                attributes: context,
            }
            let severityLevel : SeverityLevel = null;
            switch (level) {
                case LogLevelEnum.INFO:
                    severityLevel = 'info';
                    break;
                case LogLevelEnum.WARN:
                    severityLevel = 'warning';
                    break;
                case LogLevelEnum.ERROR:
                    severityLevel = 'error';
                    break;
                case LogLevelEnum.CRITICAL:
                    severityLevel = 'fatal';
                    break;
                default:
                    severityLevel = 'debug';
            }
            let messageId = null;
            // Both branches run inside withScope so extras, tags, contexts and the
            // fingerprint are applied uniformly. Previously the no-Error branch and
            // the captureMessage branch sat OUTSIDE withScope and so arrived with no
            // extras at all - losing even the hostname and the message body.
            withScope((scope) => {
                scope.setExtras(body as Record<string, unknown>);
                this.applyScopeData(scope, severityLevel, scopeData);
                if (level >= LogLevelEnum.ERROR) {
                    messageId = captureException(error || new Error(JSON.stringify(body)));
                } else {
                    // A STABLE message, not JSON.stringify(body): body embeds
                    // Date.now(), so stringifying it made every event unique and -
                    // with dedupe disabled and no fingerprint - opened a brand new
                    // Sentry issue per event.
                    messageId = captureMessage(message, severityLevel);
                }
            });
            if (!messageId) {
                return TelemetryExitCode.REJECTED;
            }
            return TelemetryExitCode.SUCCESS;
        } else {
            console.debug(message);
            return TelemetryExitCode.REJECTED;
        }
    }

}